import { useState } from 'react';

import { AlertTriangleIcon, ChevronDownIcon, ChevronUpIcon, CircleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useAlertSummary from '@/hooks/useAlertSummary';
import { cn } from '@/lib/utils';
import { DialogAlert } from '@/shared/types/dialogAlert';

import AlertSummaryItem from './AlertSummaryItem/AlertSummaryItem';

type AlertSummaryProps = { alertType: 'error' | 'warning'; alerts: DialogAlert[] };

const AlertSummary = ({ alertType, alerts }: AlertSummaryProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isWarning = alertType === 'warning';
  const { handleAlertClick } = useAlertSummary();

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'ml-1 flex items-center gap-2 rounded-md bg-inherit p-2 focus:outline-2 focus:outline-stroke',
            isWarning ? 'text-warning' : 'text-destructive'
          )}
        >
          {isWarning ? (
            <CircleAlertIcon className="text-warning" size={14} />
          ) : (
            <AlertTriangleIcon className="text-destructive" size={16} />
          )}
          <span className="text-sm font-semibold">
            {t(`dialog.alerts.${alertType}`, { 0: alerts.length, count: alerts.length })}
          </span>
          {open ? (
            <ChevronUpIcon className="text-foreground-muted" size={18} />
          ) : (
            <ChevronDownIcon className="text-foreground-muted" size={18} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2" sideOffset={3}>
        <div className="flex flex-col">
          {alerts.map((alert) => (
            <AlertSummaryItem
              key={`${alert.id}-${alert.alertField}`}
              type={alert.type}
              title={alert.title}
              body={alert.body}
              dialogId={alert.dialogId}
              handleClick={() => {
                setOpen(false);
                handleAlertClick(alert);
              }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AlertSummary;
